import type { SyncDelta } from '@couplespace/sync';

/** Which open views a batch of live deltas affects. Deltas are hints; views refetch or patch. */
export type DeltaRoute = {
  /** A page or database was created, renamed, moved or deleted. */
  sidebar: boolean;
  /** Access or membership changed; refetch everything that is open. */
  refetchAll: boolean;
  /** Changed block ids plus each changed block's parent. */
  blockIds: Set<string>;
  /** Database rows whose cells or relations changed. */
  rowIds: Set<string>;
  /** Database blocks whose `databases` record changed. */
  databaseBlockIds: Set<string>;
  /** `databases.id` values whose schema changed. */
  schemaDatabaseIds: Set<string>;
};

const NAV_BLOCK_TYPES = new Set(['page', 'database']);

function stringField(payload: Record<string, unknown> | null, key: string): string | null {
  const value = payload?.[key];
  return typeof value === 'string' ? value : null;
}

export function emptyDeltaRoute(): DeltaRoute {
  return {
    sidebar: false,
    refetchAll: false,
    blockIds: new Set(),
    rowIds: new Set(),
    databaseBlockIds: new Set(),
    schemaDatabaseIds: new Set(),
  };
}

export function routeDeltas(deltas: SyncDelta[]): DeltaRoute {
  const route = emptyDeltaRoute();

  for (const delta of deltas) {
    const payload = delta.payload;
    switch (delta.table) {
      case 'blocks': {
        route.blockIds.add(delta.rowId);
        const parentId = stringField(payload, 'parentId');
        if (parentId) {
          route.blockIds.add(parentId);
        }
        const type = stringField(payload, 'type');
        if (type === null || NAV_BLOCK_TYPES.has(type)) {
          route.sidebar = true;
        }
        break;
      }
      case 'block_properties': {
        const rowId = stringField(payload, 'blockId');
        if (rowId) {
          route.rowIds.add(rowId);
        }
        break;
      }
      case 'relations': {
        const from = stringField(payload, 'fromBlockId');
        const to = stringField(payload, 'toBlockId');
        if (from) {
          route.rowIds.add(from);
        }
        if (to) {
          route.rowIds.add(to);
        }
        break;
      }
      case 'databases': {
        const blockId = stringField(payload, 'blockId');
        if (blockId) {
          route.databaseBlockIds.add(blockId);
        }
        break;
      }
      case 'database_schemas': {
        const databaseId = stringField(payload, 'databaseId');
        if (databaseId) {
          route.schemaDatabaseIds.add(databaseId);
        }
        break;
      }
      case 'file_objects': {
        const blockId = stringField(payload, 'blockId');
        if (blockId) {
          route.blockIds.add(blockId);
        }
        break;
      }
      case 'block_acl':
      case 'members':
        route.refetchAll = true;
        route.sidebar = true;
        break;
      default:
        break;
    }
  }

  return route;
}

/** True when a page whose loaded block ids are `pageBlockIds` should react to `route`. */
export function routeTouchesPage(route: DeltaRoute, pageBlockIds: ReadonlySet<string>): boolean {
  if (route.refetchAll) {
    return true;
  }
  for (const id of route.blockIds) {
    if (pageBlockIds.has(id)) {
      return true;
    }
  }
  return false;
}

/** True when an open database view should refetch. */
export function routeTouchesDatabase(
  route: DeltaRoute,
  view: { databaseBlockId: string; schemaDatabaseId?: string | null; rowIds: ReadonlySet<string> },
): boolean {
  if (route.refetchAll) {
    return true;
  }
  if (route.blockIds.has(view.databaseBlockId) || route.databaseBlockIds.has(view.databaseBlockId)) {
    return true;
  }
  if (view.schemaDatabaseId && route.schemaDatabaseIds.has(view.schemaDatabaseId)) {
    return true;
  }
  for (const id of route.rowIds) {
    if (view.rowIds.has(id)) {
      return true;
    }
  }
  for (const id of route.blockIds) {
    if (view.rowIds.has(id)) {
      return true;
    }
  }
  return false;
}
